import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Database, Users, MapPin, Building2, Loader2,
  ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, Clock, Euro
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("access_token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadStatus = async (silent = false) => {
    try {
      if (!silent) setRefreshing(true);
      const response = await axios.get(`${API}/admin/status`, getAuthHeaders());
      setData(response.data);
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        toast.error("Bitte erneut anmelden");
        navigate("/login");
      } else if (status === 403) {
        toast.error("Admin-Rechte erforderlich");
        navigate("/app");
      } else {
        toast.error("Fehler beim Laden des Status");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const releaseLock = async () => {
    try {
      await axios.post(`${API}/admin/release-focused-lock`, {}, getAuthHeaders());
      toast.success("Lock wurde freigegeben");
      loadStatus();
    } catch {
      toast.error("Lock konnte nicht freigegeben werden");
    }
  };

  const downloadAllContactsCSV = async () => {
    try {
      toast.info("CSV wird erzeugt (read-only, keine Credits) …");
      const response = await axios.get(`${API}/admin/export-all-readonly`, {
        ...getAuthHeaders(),
        responseType: "blob",
      });
      const total = response.headers["x-total-rows"] || "?";
      const cd = response.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `adloc_alle_kontakte_${Date.now()}.csv`;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Download OK · ${total} Kontakte, read-only, 0 Credits`);
    } catch (e) {
      toast.error("Download fehlgeschlagen — " + (e.response?.data?.detail || e.message));
    }
  };


  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => loadStatus(true), 30000); // 30s
    return () => clearInterval(id);
  }, [autoRefresh]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 animate-spin text-[#2835F7]" />
      </div>
    );
  }

  if (!data) return null;

  const fp = data.focused_import;
  const progressPct = fp ? Math.round((fp.current_city_index / fp.total_cities) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/app")}
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Zur App
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <Badge variant="outline" className="ml-2">Live</Badge>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                data-testid="auto-refresh-checkbox"
              />
              Auto-Refresh (30s)
            </label>
            <Button
              size="sm"
              onClick={downloadAllContactsCSV}
              data-testid="download-all-csv-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              title="Read-only Export aller Kontakte als UTF-8 CSV — keine Credits, keine Datenänderung"
            >
              <Database className="w-4 h-4 mr-2" />
              Alle Kontakte als CSV (read-only)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => loadStatus()}
              disabled={refreshing}
              data-testid="manual-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Aktualisieren
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Top Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<Database className="w-5 h-5" />}
            label="Kontakte gesamt"
            value={data.contacts.total.toLocaleString("de-DE")}
            sub={`${data.contacts.with_email.toLocaleString("de-DE")} mit E-Mail`}
            color="from-blue-500 to-indigo-600"
            testId="stat-contacts"
          />
          <StatCard
            icon={<Users className="w-5 h-5" />}
            label="User"
            value={data.users.total}
            sub={`${data.users.verified} verifiziert · ${data.users.paying} zahlend`}
            color="from-emerald-500 to-teal-600"
            testId="stat-users"
          />
          <StatCard
            icon={<Euro className="w-5 h-5" />}
            label="Umsatz"
            value={`${data.revenue_eur.toFixed(2)} €`}
            sub="Erfolgreiche Zahlungen"
            color="from-amber-500 to-orange-600"
            testId="stat-revenue"
          />
          <StatCard
            icon={<MapPin className="w-5 h-5" />}
            label="Bundesländer"
            value={data.states.length}
            sub={`${data.industries.length} Branchen`}
            color="from-rose-500 to-pink-600"
            testId="stat-coverage"
          />
        </div>

        {/* Focused Import Status */}
        {fp && (
          <Card data-testid="focused-import-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#2835F7]" />
                  Top-50-Städte Import
                </span>
                <div className="flex items-center gap-2">
                  {fp.lock_active ? (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                      Worker läuft
                    </Badge>
                  ) : fp.remaining === 0 ? (
                    <Badge className="bg-green-100 text-green-800 border-green-300">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Fertig
                    </Badge>
                  ) : (
                    <Badge variant="outline">Idle</Badge>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">
                    Fortschritt: <strong className="text-gray-900">{fp.current_city_index} / {fp.total_cities}</strong> Städte
                  </span>
                  <span className="text-gray-600">{progressPct}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-[#2835F7] to-[#1a25c9] h-full transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <InfoBox
                  label="Verbleibend"
                  value={`${fp.remaining} Städte`}
                  hint={`~${Math.round(fp.estimated_minutes_remaining / 60)} Stunden`}
                />
                <InfoBox
                  label="Importiert via Cron"
                  value={`${fp.total_contacts_via_import.toLocaleString("de-DE")} Kontakte`}
                />
                <InfoBox
                  label="Lock Status"
                  value={fp.lock_active ? "Aktiv" : "Frei"}
                  hint={fp.lock_started_at ? `Seit ${new Date(fp.lock_started_at).toLocaleTimeString("de-DE")}` : null}
                />
              </div>

              {fp.completed_cities && fp.completed_cities.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Letzte abgeschlossene Städte:</p>
                  <div className="flex flex-wrap gap-2">
                    {fp.completed_cities.map((c, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {fp.lock_active && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={releaseLock}
                  data-testid="release-lock-btn"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Lock manuell freigeben
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Top Cities + States grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="top-cities-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#2835F7]" /> Top Städte
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.top_cities.map((c, i) => (
                  <div key={c.city} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                    <span className="text-gray-700">
                      <span className="text-gray-400 text-xs mr-2">#{i + 1}</span>
                      {c.city}
                    </span>
                    <span className="font-semibold text-gray-900">{c.count.toLocaleString("de-DE")}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="states-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#2835F7]" /> Bundesländer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {data.states.map((s) => (
                  <div key={s.state} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                    <span className="text-gray-700">{s.state}</span>
                    <span className="font-semibold text-gray-900">{s.count.toLocaleString("de-DE")}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Industries */}
        <Card data-testid="industries-card">
          <CardHeader>
            <CardTitle>Branchen-Verteilung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {data.industries.map((ind) => (
                <div key={ind.industry} className="flex justify-between bg-gray-50 px-3 py-2 rounded-lg">
                  <span className="text-gray-700 truncate">{ind.industry}</span>
                  <span className="font-semibold text-gray-900">{ind.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-gray-400 text-center pt-2">
          Letzte Aktualisierung: {new Date(data.fetched_at).toLocaleString("de-DE")}
        </p>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, sub, color, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} text-white flex items-center justify-center mb-3`}>
        {icon}
      </div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

const InfoBox = ({ label, value, hint }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="font-semibold text-gray-900">{value}</p>
    {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

export default Admin;
